import type { RegisteredWidget, WidgetId } from './registry';
import type { ResolvedDashboardWidget, WidgetConfigDoc } from './widgetConfig';

export interface WidgetDocCandidate {
  id: string;
  data: Partial<WidgetConfigDoc> | null | undefined;
}

function isValidConfigDoc(data: Partial<WidgetConfigDoc> | null | undefined): data is WidgetConfigDoc {
  if (!data || typeof data !== 'object') return false;
  if (!data.config || typeof data.config !== 'object') return false;
  return true;
}

/**
 * Merge stored per-widget config docs onto the registry. Returns ALL registered
 * widgets in registry order, each carrying its stored doc (or `null` when the
 * user has never configured it). Placement/visibility is owned by the fixed
 * layout in `DashboardGrid` — this helper is config-only.
 */
export function mergeWidgetConfigs(
  candidates: readonly WidgetDocCandidate[],
  registry: Record<WidgetId, RegisteredWidget>,
): ResolvedDashboardWidget[] {
  const byId = new Map<WidgetId, WidgetConfigDoc>();
  for (const candidate of candidates) {
    if (!(candidate.id in registry)) continue;
    if (!isValidConfigDoc(candidate.data)) continue;
    byId.set(candidate.id as WidgetId, candidate.data);
  }

  return (Object.keys(registry) as WidgetId[]).map((id) => ({
    id,
    registry: registry[id],
    doc: byId.get(id) ?? null,
  }));
}
